import React from 'react'
import { ClickAwayListener, Popper } from '@mui/material'
import { useAppDispatch, useAppSelector } from '../../../../hooks/redux'

import ShowMoreLess from '../../../atoms/ShowMoreLess'
import MediaAvatar from '../../../atoms/MediaAvatar'
import OverflowText from '../../../atoms/OverflowText'
import DownArrowSmallIcon from '../../../icons/DownArrowSmallIcon'
import useDelayedUserSearch from '../../../../hooks/useDelayedUserSearch/v2'
import Result from '../../../organisms/ChatWindowV2/Header/EditParticipantsModal/Result'
import CustomFieldsSection from './CustomFieldsSection'
import StatusOption from '../../../atoms/StatusOption'
import UserChannels from '../../UserChannels'
import CopyableStateProvider from '../../../../providers/CopyableStateProvider'

import {
  selectUserStatus,
  fetchUserStatus
} from '../../../../slices/userStatus'
import { useQueueState } from '../../../../providers/QueueStateProvider'
import { useUserAPI } from '../../../../providers/UserAPIProvider'
import {
  TYPES,
  changeType,
  setChannelValue
} from '../../../../slices/chatBoxSlice'
import useAccess, { PERMS } from '../../../../hooks/useAccess'
import { useEnvironment } from '../../../../providers/EnvironmentProvider'
import { formatSessionForPut } from '../../../../utilities'
import { updateSession } from '../../../../lib/api'
import { AddErrorAlert } from '../../../../slices/alert'
import { checkConflicts } from '../../../../slices/convo'
import useKeyListener from '@SonarCX/hooks/useKeyListener'
import useStyles from './styles'

interface ProfileV2Props {
  user: any
  convoOverride?: any
}

const ProfileV2: React.FC<ProfileV2Props> = ({ user, convoOverride }) => {
  const dispatch = useAppDispatch()
  const { conversation: realConvo } = useQueueState()

  const {
    statuses,
    loaded: statusesLoaded,
    loading: statusesLoading
  } = useAppSelector(selectUserStatus)

  const { allUsers, loadUser, uploadUser, updateUsers } = useUserAPI()
  const { user: currentUser } = useEnvironment()
  const { search, results, noResults, setNoResults } =
    useDelayedUserSearch(false)

  const hasPermission = useAccess()

  const conversation = React.useMemo(
    () => convoOverride ?? realConvo,
    [convoOverride, realConvo]
  )

  const asignee = allUsers[conversation?.assigned_to]
  const [expanded, setExpanded] = React.useState(false)
  const [disabled, setDisabled] = React.useState(false)
  const [showAssignee, setShowAssignee] = React.useState(false)
  const [showStatus, setShowStatus] = React.useState(false)
  const [searchValue, setSearchValue] = React.useState('')

  useKeyListener({
    key: 'Escape',
    callback: () => {
      setShowAssignee(false)
      setShowStatus(false)
    }
  })

  const assigneeRef = React.useRef()
  const statusRef = React.useRef()
  const inputRef = React.useRef<HTMLInputElement | null>(null)

  const canChangeChannel = React.useMemo(
    () =>
      !conversation.ended_at && hasPermission(PERMS.ConversationsMultichannel),
    [conversation.ended_at, hasPermission]
  )

  const classes = useStyles({ canChangeChannel })

  if (searchValue.length <= 2 && noResults) {
    setNoResults(false)
  }

  // Loads assignee
  React.useEffect(() => {
    if (!asignee) {
      loadUser(conversation.assigned_to)
    }
  }, [loadUser, asignee, conversation.assigned_to])

  // Loads user statuses if needed
  React.useEffect(() => {
    if (!statusesLoaded && !statusesLoading) {
      dispatch(fetchUserStatus())
    }
  }, [statusesLoaded, statusesLoading, dispatch])

  React.useEffect(() => {
    if (user?.restricted) {
      setDisabled(true)
    }
  }, [user])

  React.useEffect(() => {
    if (results.length > 0) {
      updateUsers(results)
    }
  }, [updateUsers, results])

  const onSearchChange = React.useCallback(
    value => {
      setSearchValue(value)
      search(value)
    },
    [search]
  )

  // Change type and select channel as 'to'
  const handleChannelClick = React.useCallback(
    (isPhone, id) => {
      if (canChangeChannel) {
        dispatch(changeType(isPhone ? TYPES.SMS : TYPES.EMAIL))
        dispatch(setChannelValue({ isPhone, id }))
      }
    },
    [canChangeChannel, dispatch]
  )

  const openAssignee = React.useCallback(() => {
    setShowAssignee(true)
    setTimeout(() => inputRef?.current?.focus(), 0)
  }, [])

  const hideShowAssignee = React.useCallback(() => {
    setShowAssignee(false)
  }, [])

  const openStatus = React.useCallback(() => {
    setShowStatus(true)
  }, [])

  const hideStatus = React.useCallback(() => {
    setShowStatus(false)
  }, [])

  const canEdit = React.useMemo(() => {
    if (hasPermission(PERMS.ContactsFull)) {
      return true
    }

    if (user && hasPermission(PERMS.ContactsOwn)) {
      const { owner_id } = user
      return owner_id === currentUser?.id
    }

    return false
  }, [hasPermission, user, currentUser])

  const handleStatusChange = React.useCallback(
    val => {
      hideStatus()
      const copy = {
        ...user,
        status_id: val
      }

      if (canEdit) {
        uploadUser(copy)
      }
    },
    [user, canEdit, hideStatus, uploadUser]
  )

  // Updates assignee
  const handleAssigneChange = React.useCallback(
    async assigned_to => {
      const current = formatSessionForPut(conversation)

      if (current.assigned_to === assigned_to) return

      const newSession = { ...current, assigned_to }
      const { id, ...rest } = newSession

      delete rest.userIds
      //@ts-ignore
      delete rest.verified

      try {
        // @ts-ignore
        await updateSession(id, rest)
      } catch (err) {
        console.error('Error assigning', err)
        dispatch(AddErrorAlert({ err }))
      }
    },
    [conversation, dispatch]
  )

  // Handle conflict checks when a channel is added
  const userID = React.useMemo(() => user?.id ?? '', [user])
  const channelAdded = React.useCallback(() => {
    dispatch(checkConflicts(userID))
  }, [dispatch, userID])

  return (
    <div className={classes.root}>
      <CopyableStateProvider>
        <UserChannels
          canAdd
          hideIcon
          user={user}
          canChangeChannel={canChangeChannel}
          handleChannelClick={handleChannelClick}
          onChannelAdd={channelAdded}
        />
      </CopyableStateProvider>
      {/* Assigned to */}
      <div className={classes.row}>
        <div className={classes.title} ref={assigneeRef}>
          Assigned
        </div>

        {asignee ? (
          <>
            <MediaAvatar
              hideCard={false}
              user={asignee}
              className={classes.smallAvatar}
            />

            <OverflowText className={classes.data}>
              {asignee?.name ?? 'Loading...'}
            </OverflowText>
          </>
        ) : (
          <OverflowText className={classes.data}>No one</OverflowText>
        )}

        {!disabled && (
          <DownArrowSmallIcon
            onClick={openAssignee}
            className={classes.actionIcon}
          />
        )}

        {showAssignee && (
          <ClickAwayListener onClickAway={hideShowAssignee}>
            <Popper
              open={showAssignee}
              disablePortal={false}
              anchorEl={assigneeRef?.current}
              style={{ zIndex: '1500' }}
              placement='bottom-start'
            >
              <div className={classes.optionList}>
                <input
                  ref={inputRef}
                  className={classes.asigneeSearch}
                  placeholder='Search...'
                  onChange={e => {
                    onSearchChange(e.target.value)
                  }}
                />

                {noResults && (
                  <div className={classes.noResult}>No results</div>
                )}

                {results.map(user => (
                  <div
                    key={user.id}
                    onClick={() => {
                      handleAssigneChange(user.id)
                      // Reset state
                      setSearchValue('')
                      search('')
                      hideShowAssignee()
                    }}
                    className={classes.option}
                  >
                    <Result user={user} />
                  </div>
                ))}
              </div>
            </Popper>
          </ClickAwayListener>
        )}
      </div>

      {/* Status */}
      <div className={classes.row}>
        <div className={classes.title} ref={statusRef}>
          Status
        </div>

        <OverflowText className={classes.data}>
          <StatusOption
            id={user?.status_id}
            statuses={statuses}
            loaded={statusesLoaded}
            noStatusLabel='Unassigned'
          />
        </OverflowText>

        {!disabled && (
          <DownArrowSmallIcon
            className={classes.actionIcon}
            onClick={openStatus}
          />
        )}

        {showStatus && (
          <ClickAwayListener onClickAway={hideStatus}>
            <Popper
              open={showStatus}
              disablePortal={false}
              anchorEl={statusRef?.current}
              style={{ zIndex: '1500' }}
              placement='bottom-start'
            >
              <div className={classes.optionList}>
                {statuses.map(status => (
                  <div
                    className={classes.statusOptionContainer}
                    key={status.id}
                    onClick={() => handleStatusChange(status.id)}
                  >
                    <StatusOption
                      id={status.id}
                      statuses={statuses}
                      loaded={statusesLoaded}
                      rootClass={classes.statusOption}
                    />
                  </div>
                ))}
              </div>
            </Popper>
          </ClickAwayListener>
        )}
      </div>

      {expanded && (
        <CustomFieldsSection user={user} external={true} canEdit={canEdit} />
      )}

      <ShowMoreLess expanded={expanded} setExpanded={setExpanded} />
    </div>
  )
}

export default ProfileV2
